/// <reference types="google.maps" />
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { ArrowLeft, Plus, MapPin, BadgeCheck, Crown, Phone, Mail, Globe, Instagram, ShieldAlert, Loader2, Locate, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { useGoogleMapsLoader } from '@/hooks/useGoogleMapsLoader';
import { PetPlace, PLACE_CATEGORIES, PlaceCategory, usePetPlaces } from '@/hooks/usePetPlaces';
import AddPlaceDialog from '@/components/places/AddPlaceDialog';
import ClaimPlaceDialog from '@/components/places/ClaimPlaceDialog';
import DisputeClaimDialog from '@/components/places/DisputeClaimDialog';
import { toast } from '@/hooks/use-toast';
import { getRobustPosition } from '@/hooks/useGeolocation';

const DEFAULT_CENTER = { lat: 19.4326, lng: -99.1332 };

function categoryEmoji(c: string | null): string {
  switch (c) {
    case 'dog_park': return '🐕';
    case 'cat_cafe': return '🐈';
    case 'dog_cafe': return '☕';
    case 'pet_shop': return '🛍️';
    case 'restaurant': return '🍽️';
    case 'veterinary': return '🩺';
    case 'grooming': return '✂️';
    case 'daycare': return '🏨';
    case 'training': return '🎓';
    case 'dog_walker': return '🦮';
    default: return '📍';
  }
}

export default function PetFriendlyPlaces() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isLoaded, error } = useGoogleMapsLoader();

  // Mark feature as visited so promo banners auto-hide elsewhere
  useEffect(() => {
    try { localStorage.setItem('pf_places_visited', '1'); } catch {}
  }, []);

  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const userMarkerRef = useRef<google.maps.Marker | null>(null);

  const [userLat, setUserLat] = useState<number | null>(null);
  const [userLng, setUserLng] = useState<number | null>(null);
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [radiusKm, setRadiusKm] = useState(5);
  const [category, setCategory] = useState<PlaceCategory | null>(null);

  const [selected, setSelected] = useState<PetPlace | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);

  // Robust native-first GPS with permissions check, services check,
  // and automatic high->low accuracy fallback (handles indoor / weak signal).
  const requestNativeLocation = useCallback(async (): Promise<{ lat: number; lng: number } | null> => {
    const res = await getRobustPosition();
    if (res.ok && res.latitude != null && res.longitude != null) {
      return { lat: res.latitude, lng: res.longitude };
    }
    let desc = t('places.errors.gpsDesc', 'Showing default area. Enable GPS for better results.');
    const reason = res.reason;
    if (reason === 'services_disabled') {
      desc = t(
        'places.errors.gpsServicesDisabled',
        'Location services are off. Turn on GPS in your device settings.'
      );
    } else if (reason === 'permission_denied') {
      desc = t(
        'places.errors.gpsPermissionDenied',
        'Location permission denied. Allow location for this app in settings.'
      );
    } else if (reason === 'timeout') {
      desc = t(
        'places.errors.gpsTimeout',
        'GPS is taking too long. Try again outdoors or near a window.'
      );
    }
    toast({
      title: t('places.errors.gpsTitle', 'Location unavailable'),
      description: desc,
    });
    return null;
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const c = await requestNativeLocation();
      if (cancelled) return;
      if (c) {
        setUserLat(c.lat);
        setUserLng(c.lng);
        setCenter(c);
      } else {
        setCenter(DEFAULT_CENTER);
      }
    })();
    return () => { cancelled = true; };
  }, [requestNativeLocation]);

  const { data: places, isLoading } = usePetPlaces({
    lat: center?.lat ?? null,
    lng: center?.lng ?? null,
    radiusKm,
    category,
  });

  // Init map
  useEffect(() => {
    if (!isLoaded || !mapDivRef.current || !center || mapRef.current) return;
    mapRef.current = new google.maps.Map(mapDivRef.current, {
      center,
      zoom: 13,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: false,
      rotateControl: false,
      scaleControl: false,
      gestureHandling: 'greedy',
      clickableIcons: false,
    });

    // Recenter button via idle: track center for refetch when moved significantly
    let lastFetchCenter = { ...center };
    mapRef.current.addListener('idle', () => {
      const c = mapRef.current!.getCenter();
      if (!c) return;
      const lat = c.lat();
      const lng = c.lng();
      const distKm = haversine(lastFetchCenter.lat, lastFetchCenter.lng, lat, lng);
      if (distKm > radiusKm * 0.25) {
        lastFetchCenter = { lat, lng };
        setCenter({ lat, lng });
      }
    });
  }, [isLoaded, center, radiusKm]);

  // User marker (custom paw pin)
  useEffect(() => {
    if (!mapRef.current || userLat == null || userLng == null) return;
    if (userMarkerRef.current) userMarkerRef.current.setMap(null);
    const size = 44;
    userMarkerRef.current = new google.maps.Marker({
      position: { lat: userLat, lng: userLng },
      map: mapRef.current,
      icon: {
        url: '/markers/user-location.png',
        scaledSize: new google.maps.Size(size, size * 1.36),
        anchor: new google.maps.Point(size / 2, size * 1.36),
      },
      title: t('places.youAreHere', 'You are here'),
      zIndex: 9999,
    });
  }, [userLat, userLng, isLoaded, t]);

  // Render markers + clustering
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;
    // clear
    if (clustererRef.current) clustererRef.current.clearMarkers();
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    if (!places || places.length === 0) return;

    const iconUrlFor = (category: string | null): string => {
      switch (category) {
        case 'dog_park':
          return '/markers/park.png';
        case 'cat_cafe':
        case 'dog_cafe':
          return '/markers/cafe.png';
        case 'pet_shop':
          return '/markers/store.png';
        case 'restaurant':
          return '/markers/restaurant.png';
        case 'veterinary':
          return '/markers/veterinary.png';
        case 'grooming':
          return '/markers/grooming.png';
        case 'daycare':
          return '/markers/daycare.png';
        case 'training':
          return '/markers/training.png';
        case 'dog_walker':
          return '/markers/dog-walker.png';
        default:
          return '/markers/store.png';
      }
    };

    const markers = places.map((p) => {
      const size = p.is_premium ? 48 : 38;
      const marker = new google.maps.Marker({
        position: { lat: p.latitude, lng: p.longitude },
        title: p.name,
        icon: {
          url: iconUrlFor(p.category),
          scaledSize: new google.maps.Size(size, size),
          anchor: new google.maps.Point(size / 2, size),
        },
        zIndex: p.is_premium ? 1000 : 100,
      });
      marker.addListener('click', () => setSelected(p));
      return marker;
    });
    markersRef.current = markers;
    clustererRef.current = new MarkerClusterer({ map: mapRef.current!, markers });
  }, [places, isLoaded]);

  const handleRecenter = useCallback(async () => {
    if (userLat == null || userLng == null) {
      const c = await requestNativeLocation();
      if (c) {
        setUserLat(c.lat);
        setUserLng(c.lng);
        setCenter(c);
        mapRef.current?.panTo(c);
      } else {
        toast({
          title: t('places.errors.gpsTitle', 'Location unavailable'),
          description: t('places.errors.gpsDesc', 'Showing default area. Enable GPS for better results.'),
        });
      }
      return;
    }
    const c = { lat: userLat, lng: userLng };
    mapRef.current?.panTo(c);
    mapRef.current?.setZoom(14);
    setCenter(c);
  }, [userLat, userLng, requestNativeLocation, t]);

  const handleAdd = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (userLat == null || userLng == null) {
      handleRecenter();
      toast({ title: t('places.errors.gpsRequired', 'Location required') });
      return;
    }
    setAddOpen(true);
  };

  const categoryChips = useMemo(
    () => [{ id: null as PlaceCategory | null, label: t('places.categories.all', 'All') }, ...PLACE_CATEGORIES.map((c) => ({ id: c, label: t(`places.categories.${c}`, c) }))],
    [t]
  );

  return (
    <div className="relative h-[calc(100dvh-3.5rem)] w-full overflow-hidden bg-background">
      {/* Header */}
      <div
        className="absolute top-0 left-0 right-0 z-20 bg-background/95 backdrop-blur-sm border-b"
      >

        <div className="flex items-center gap-2 px-3 py-2">
          <Button size="icon" variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-base font-semibold flex-1 truncate">
            {t('places.title', 'Pet Friendly Places')}
          </h1>
          <div className="w-9" aria-hidden />

        </div>
        {/* Chips */}
        <div className="flex gap-2 overflow-x-auto px-3 pb-2 no-scrollbar">
          {categoryChips.map((c) => {
            const active = category === c.id;
            return (
              <button
                key={c.id ?? 'all'}
                onClick={() => setCategory(c.id)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted'
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
        {/* Slider */}
        <div className="px-3 pb-2 flex items-center gap-3">
          <span className="text-[11px] text-muted-foreground">{t('places.radius', 'Radius')}</span>
          <Slider
            value={[radiusKm]}
            min={1}
            max={20}
            step={1}
            onValueChange={(v) => setRadiusKm(v[0])}
            className="flex-1"
          />
          <span className="text-xs font-medium w-12 text-right">{t('places.distanceKm', { count: radiusKm })}</span>
        </div>
      </div>

      {/* Map */}
      <div
        className="absolute inset-0"
        style={{ paddingTop: '112px' }}

      >
        {error ? (
          <div className="p-4 text-center text-sm text-destructive">
            {t('places.errors.mapLoad', 'Could not load map')}: {error}
          </div>
        ) : !isLoaded || !center ? (
          <Skeleton className="w-full h-full" />
        ) : (
          <div ref={mapDivRef} className="w-full h-full" />
        )}

        {isLoading && (
          <div className="absolute top-2 right-2 bg-background/90 rounded-full p-2 shadow z-10">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          </div>
        )}
      </div>

      {/* Floating Recenter (bottom-left, above Google attribution) */}
      <Button
        size="icon"
        variant="secondary"
        onClick={handleRecenter}
        aria-label={t('places.recenter', 'Recenter')}
        className="absolute left-4 z-30 rounded-full h-11 w-11 shadow-lg bg-background/95 hover:bg-background border"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 6rem)' }}
      >
        <Locate className="h-5 w-5" />
      </Button>

      {/* FAB */}
      <Button
        onClick={handleAdd}
        className="absolute right-4 z-30 rounded-full h-14 px-5 shadow-lg gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 6rem)' }}
      >
        <Plus className="h-5 w-5" />
        {t('places.addFab', 'Add place')}
      </Button>

      {/* Detail Sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh] overflow-y-auto">
          {selected && (
            <>
              <SheetHeader className="text-left">
                <SheetTitle className="flex items-center gap-2">
                  <span>{categoryEmoji(selected.category)}</span>
                  <span className="flex-1">{selected.name}</span>
                  {selected.provider_id && (
                    <Badge className="bg-emerald-600 text-white border-0 gap-1">
                      <Store className="h-3 w-3" />
                      {t('places.badges.verifiedBusiness', 'Verified Business')}
                    </Badge>
                  )}
                  {selected.is_premium && !selected.provider_id && (
                    <Badge className="bg-amber-500 text-white border-0 gap-1">
                      <Crown className="h-3 w-3" />
                      {t('places.badges.premium', 'Premium')}
                    </Badge>
                  )}
                  {selected.is_verified && !selected.is_premium && (
                    <Badge variant="secondary" className="gap-1">
                      <BadgeCheck className="h-3 w-3" /> {t('places.badges.verified', 'Verified')}
                    </Badge>
                  )}
                </SheetTitle>
                <SheetDescription className="text-xs">
                  {t(`places.categories.${selected.category ?? 'all'}`, selected.category ?? '')}
                  {' · '}
                  {t('places.distanceKmPrecise', { distance: (selected.distance_m / 1000).toFixed(2) })}
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-3 mt-3">
                {selected.address && (
                  <div className="text-xs text-muted-foreground flex items-start gap-1">
                    <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>{selected.address}</span>
                  </div>
                )}
                {selected.pet_policy_summary && (
                  <p className="text-sm">{selected.pet_policy_summary}</p>
                )}

                {selected.is_premium && selected.claim_contact && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {selected.claim_contact.phone && (
                      <a href={`tel:${selected.claim_contact.phone}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-muted">
                        <Phone className="h-3 w-3" /> {selected.claim_contact.phone}
                      </a>
                    )}
                    {selected.claim_contact.email && (
                      <a href={`mailto:${selected.claim_contact.email}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-muted">
                        <Mail className="h-3 w-3" /> {selected.claim_contact.email}
                      </a>
                    )}
                    {selected.claim_contact.website && (
                      <a href={selected.claim_contact.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-muted">
                        <Globe className="h-3 w-3" /> {t('places.contact.website', 'Website')}
                      </a>
                    )}
                    {selected.claim_contact.instagram && (
                      <a href={`https://instagram.com/${String(selected.claim_contact.instagram).replace(/^@/, '')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-muted">
                        <Instagram className="h-3 w-3" /> {selected.claim_contact.instagram}
                      </a>
                    )}
                  </div>
                )}

                <div className="pt-2 flex flex-col gap-2">
                  {!selected.owner_id && !selected.provider_id ? (
                    <Button
                      onClick={() => {
                        if (!user) {
                          navigate('/auth');
                          return;
                        }
                        setClaimOpen(true);
                      }}
                      className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
                    >
                      <Crown className="h-4 w-4" />
                      {t('places.claim.cta', 'Are you the owner? Claim it for free')}
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => {
                      if (!user) {
                        navigate('/auth');
                        return;
                      }
                      setDisputeOpen(true);
                    }}
                  >
                    <ShieldAlert className="h-4 w-4" />
                    {t('places.report.cta', 'Report a problem (incorrect, duplicate, closed)')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AddPlaceDialog open={addOpen} onOpenChange={setAddOpen} userLat={userLat} userLng={userLng} />
      <ClaimPlaceDialog
        open={claimOpen}
        onOpenChange={setClaimOpen}
        placeId={selected?.id ?? null}
        placeName={selected?.name}
      />
      <DisputeClaimDialog
        open={disputeOpen}
        onOpenChange={setDisputeOpen}
        placeId={selected?.id ?? null}
      />
    </div>
  );
}

function haversine(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
